import React from 'react';

import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import { useFormHook } from '../../hooks';
import { useWizardContext } from './logic/WizardContext';
import { AddressFormStep } from './steps/AddressFormStep';
import ChangePersonalAddressHeader from './ChangePersonalAddressHeader';
import { ErrorView } from './steps/ErrorView';
import { EditView } from './steps/EditView';
import { createSchema } from './logic/ChangePersonalAddressSchema';
import { useChangeAddressStepHook } from './logic/useStepHook';
import { useHandleSucces as useHandleSuccess } from './logic/useHandleSuccess';
import useStore from '../../store';
import { CloseButton, CloseButtonColor, CloseButtonSize } from '../../DesignSystem/LiliCloseButton/CloseButton';

export enum ChangePersonalAddressFlow {
  AddressForm,

  Edit,
  ERROR,
  NONE,
}
export interface ChangePersonalAddressProps {
  onClose: () => void;
  businessFlow: boolean;
}
export const ChangePersonalAddressSteps = ({ onClose, businessFlow }: ChangePersonalAddressProps) => {
  const { step } = useWizardContext();
  const summary = useStore((state) => state.summary);

  const { address, businessAddress } = summary.customer;
  const personalAddressFields = {
    street: address?.line1,
    apt: address?.line2,
    city: address?.city,
    state: address?.state,
    zip: address?.postalCode,
  };
  const businessAddressFields = {
    street: businessAddress?.line1,
    apt: businessAddress?.line2,
    city: businessAddress?.city,
    state: businessAddress?.state,
    zip: businessAddress?.postalCode,
  };
  const formDefaultValues = businessFlow ? businessAddressFields : personalAddressFields;
  const { t } = useTranslation();

  const changePersonalAddressSchema = createSchema(t);
  const { setError, formMethods } = useFormHook({
    formSchema: changePersonalAddressSchema,
    formDefaultValues,
    schemaTitle: 'change-address',
  });
  const { getValues } = formMethods;
  const { handleErrorCode, customerId, handleEdit } = useChangeAddressStepHook({ setError });

  const { isLoading, handleConfirm } = useHandleSuccess({
    businessFlow,
    onClose,
    getValues,
    t,
    handleErrorCode,
    customerId,
  });

  const renderMap: { [key in ChangePersonalAddressFlow]: React.ReactNode } = {
    [ChangePersonalAddressFlow.AddressForm]: <AddressFormStep control={formMethods.control} />,

    [ChangePersonalAddressFlow.Edit]: (
      <EditView isLoading={isLoading} handleClick={handleConfirm} handleEdit={handleEdit} />
    ),
    [ChangePersonalAddressFlow.ERROR]: <ErrorView onClose={onClose} />,
    [ChangePersonalAddressFlow.NONE]: <div />,
  };

  return (
    <div>
      <div className='flex flex-col bg-bw01 rounded-[12px] maxHeight-[460px]  animate-fadeIn px-[32px] pt-[32px] pb-[32px] w-[504px] '>
        <CloseButton
          onClick={onClose}
          id='closeButton'
          color={CloseButtonColor.Black}
          size={CloseButtonSize.Medium}
          className='absolute top-[16px] right-[16px] w-[0.625rem] h-[0.625rem] cursor-pointer z-10'
        />
        <ChangePersonalAddressHeader businessFlow={businessFlow} />
        <div className='flex flex-col justify-center items-center  '>
          <FormProvider {...formMethods}>{renderMap[step as ChangePersonalAddressFlow]}</FormProvider>
        </div>
      </div>
    </div>
  );
};
